/**
 * <store-hours> - reveals an "Open Now" or "Currently closed" status for the retail store.
 *
 * The decision is made here rather than in Liquid on purpose. Liquid's `now` is UTC, so
 * comparing it against Ottawa opening hours is wrong by 4-5 hours and shifts twice a year at
 * daylight saving; storefront HTML can also be served from cache, which would freeze a
 * server-rendered status. `Intl.DateTimeFormat` with the store's IANA zone is correct
 * year-round, and re-checking on an interval keeps a long-open tab honest.
 *
 * Schedule and copy come from the "Store hours" theme settings via the JSON config that
 * snippets/store-hours.liquid emits, so this file never needs editing to change hours.
 */

/** Minutes between re-checks. Cheap, and catches the store opening or closing mid-visit. */
const RECHECK_INTERVAL_MS = 60_000;

class StoreHours extends HTMLElement {
  /** @type {ReturnType<typeof setInterval> | undefined} */
  #timer;

  /** @type {Intl.DateTimeFormat | undefined} */
  #formatter;

  /** @type {StoreHoursConfig | null} */
  #config = null;

  connectedCallback() {
    this.#config = this.#readConfig();
    if (!this.#config) return;

    try {
      this.#formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: this.#config.timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    } catch {
      // An invalid time zone in theme settings would throw. Leave both states hidden rather
      // than risk telling a shopper the store is open when it is not.
      return;
    }

    this.#render();
    this.#timer = setInterval(() => this.#render(), RECHECK_INTERVAL_MS);
  }

  disconnectedCallback() {
    if (this.#timer) clearInterval(this.#timer);
    this.#timer = undefined;
  }

  /**
   * @returns {StoreHoursConfig | null}
   */
  #readConfig() {
    const script = this.querySelector('script[data-store-hours-config]');
    if (!script?.textContent) return null;

    try {
      const parsed = JSON.parse(script.textContent);
      if (!Array.isArray(parsed?.days) || parsed.days.length !== 7) return null;
      return parsed;
    } catch {
      return null;
    }
  }

  /**
   * Reads the current date and time as the store experiences it.
   * @returns {{ date: string, weekday: number, minutes: number } | null}
   */
  #storeNow() {
    if (!this.#formatter) return null;

    /** @type {Record<string, string>} */
    const parts = {};
    for (const { type, value } of this.#formatter.formatToParts(new Date())) {
      parts[type] = value;
    }

    const year = Number(parts.year);
    const month = Number(parts.month);
    const day = Number(parts.day);
    // `hour12: false` yields 24 in some engines for midnight; normalize it to 0.
    const hour = Number(parts.hour) % 24;
    const minute = Number(parts.minute);
    if ([year, month, day, hour, minute].some(Number.isNaN)) return null;

    // Derive the weekday arithmetically from the store-local calendar date rather than reading
    // Intl's weekday name: no dependency on how a given ICU build spells "Mon". Date.UTC is
    // only doing calendar maths here - the parts are already in the store's zone.
    const weekday = (new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 6) % 7;

    return {
      date: `${parts.year}-${parts.month}-${parts.day}`,
      weekday,
      minutes: hour * 60 + minute,
    };
  }

  #render() {
    const config = this.#config;
    const now = this.#storeNow();
    if (!config || !now) return;

    // A dated exception wins over the weekly schedule.
    const exception = config.exceptions?.find((entry) => entry.date === now.date);
    const today = exception ?? config.days[now.weekday];
    if (!today) return;

    const isOpen = withinHours(now.minutes, today.open, today.close);

    const openEl = this.querySelector('[data-store-hours-state="open"]');
    const closedEl = this.querySelector('[data-store-hours-state="closed"]');
    const todayEl = this.querySelector('[data-store-hours-today]');

    if (todayEl) {
      // Only the hours value. The "Today's hours:" label is rendered by Liquid, which keeps its
      // apostrophe out of the JSON config - the `t` filter HTML-escapes it, and textContent
      // would then print the raw &#39; entity.
      todayEl.textContent = config.timeZoneLabel ? `${today.label} ${config.timeZoneLabel}` : today.label;
    }

    if (openEl instanceof HTMLElement) openEl.hidden = !isOpen;
    if (closedEl instanceof HTMLElement) closedEl.hidden = isOpen;

    this.#markTodayInLists(now.weekday);
  }

  /**
   * Bolds today's row in any weekly list on the page (the pickup drawer's list).
   * @param {number} weekday
   */
  #markTodayInLists(weekday) {
    for (const item of document.querySelectorAll('[data-store-hours-day]')) {
      item.classList.toggle('store-hours-list__item--today', item.getAttribute('data-store-hours-day') === String(weekday));
    }
  }
}

/**
 * True when `minutes` falls inside the range, handling a range that crosses midnight.
 * An open value of -1 means closed all day.
 * @param {number} minutes
 * @param {number} open
 * @param {number} close
 */
function withinHours(minutes, open, close) {
  if (open < 0 || close < 0) return false;
  if (close > open) return minutes >= open && minutes < close;
  // Crosses midnight (e.g. 18:00-01:00): open late tonight or early this morning.
  if (close < open) return minutes >= open || minutes < close;
  return false;
}

if (!customElements.get('store-hours')) {
  customElements.define('store-hours', StoreHours);
}

/**
 * @typedef {object} StoreHoursDay
 * @property {number} open - Minutes past midnight, or -1 when closed.
 * @property {number} close - Minutes past midnight, or -1 when closed.
 * @property {string} label - Customer-facing label, e.g. "11am - 7pm".
 */

/**
 * @typedef {StoreHoursDay & { date: string, note: string }} StoreHoursException
 */

/**
 * @typedef {object} StoreHoursConfig
 * @property {string} timeZone
 * @property {string} timeZoneLabel
 * @property {string} closedLabel
 * @property {StoreHoursDay[]} days
 * @property {StoreHoursException[]} [exceptions]
 */
